<template>
  <div
    class="grid grid-cols-12 gap-5 place-items-center lg:place-items-stretch place-content-center md:h-screen mt-5 md:mt-0"
  >
    <section
      v-if="!hasError"
      class="col-span-12 lg:col-span-6 w-full sm:w-8/12 lg:w-full"
    >
      <div class="bg-white shadow-md rounded-xl overflow-hidden lg:m-10">
        <div class="bg-gray-50 p-5">
          <h4>
            {{
              $t("labels.generic", {
                pt: "Registre-se como aluno",
                en: "Register as a student",
              })
            }}
          </h4>
        </div>
        <form @submit.prevent="sendEmailToUser()">
          <div class="px-5 py-5">
            <div class="flex flex-col my-4">
              <InputText
                id="name-input"
                type="text"
                v-model="form.name"
                :placeholder="
                  $t('labels.generic', { pt: 'Nome completo', en: 'Full name' })
                "
                :class="v$.form.name.$error ? 'p-invalid' : ''"
              />
              <Errors :errors="v$.form.name.$errors" />
            </div>
            <div class="flex flex-col my-4">
              <InputMask
                id="cpf-input"
                mask="999.999.999-99"
                v-model="form.cpf"
                placeholder="CPF"
                :class="v$.form.cpf.$error ? 'p-invalid' : ''"
              />
              <Errors :errors="v$.form.cpf.$errors" />
            </div>
            <div class="flex flex-col my-4">
              <InputPassword
                id="password-input"
                v-model="form.password"
                :feedback="false"
                toggleMask
                :placeholder="
                  $t('labels.generic', { pt: 'Senha', en: 'Password' })
                "
                :class="v$.form.password.$error ? 'p-invalid' : ''"
              />
              <Errors :errors="v$.form.password.$errors" />
            </div>
            <div class="flex flex-col my-4">
              <InputPassword
                id="confirm-password-input"
                v-model="form.confirmPassword"
                :feedback="false"
                toggleMask
                :placeholder="
                  $t('labels.generic', {
                    pt: 'Confirmar senha',
                    en: 'Confirm password',
                  })
                "
                :class="v$.form.confirmPassword.$error ? 'p-invalid' : ''"
              />
              <Errors :errors="v$.form.confirmPassword.$errors" />
            </div>
            <div class="flex justify-start flex-wrap gap-1">
              <label for="binary" class="font-[600]">
                {{
                  $t("labels.generic", {
                    pt: "Já tem uma conta? Faça o",
                    en: "Already have an account?",
                  })
                }}
              </label>
              <a
                @click="go.redirectTo('sign-in')"
                class="text-h-primary-500 hover:text-h-primary-700 cursor-pointer font-[700]"
              >
                Login
              </a>
            </div>
          </div>

          <div
            class="bg-gray-50 p-4 flex justify-end items-center mt-3 border-t border-t-gray-300 rounded-b-lg"
          >
            <Button type="submit" label="Enviar" class="btn-primary" />
          </div>
        </form>
      </div>
    </section>

    <section
      v-if="!hasError"
      class="hidden lg:flex justify-end lg:col-span-6 lg:m-40"
    >
      <ComputerComponent icon="md-assignmentind-twotone" />
    </section>

    <section v-if="hasError" id="sign-up-error" class="col-span-12">
      <div class="flex justify-center items-center">
        <div
          class="bg-white shadow-sm rounded-md border border-gray-300 md:w-5/12"
        >
          <div class="bg-gray-50 border-b border-gray-300 p-4">
            <h4>
              {{
                $t("labels.generic", {
                  pt: "Link expirado ou inválido",
                  en: "Expired or invalid link",
                })
              }}
            </h4>
          </div>
          <div class="flex flex-col justify-center items-center gap-3 my-10">
            <v-icon
              name="md-announcement-twotone"
              class="text-h-primary-500 my-5"
              scale="8"
            />

            <p
              class="px-8 md:px-10 text-center text-lg"
              v-html="
                $t('labels.generic', {
                  pt: 'Seu Link expirou, <strong>foi enviado um novo e-mail</strong> para completar o cadastro!',
                  en: 'Your Link has expired, <strong>a new email has been sent</strong> to complete the registration!',
                })
              "
            ></p>
          </div>
          <div
            class="bg-gray-50 flex justify-end items-center mt-3 border-t border-t-gray-300 rounded-b-lg p-5"
          >
            <Button
              id="login"
              :label="'Voltar para o login'"
              class="btn-primary"
              @click.prevent="go.redirectTo('sign-in')"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { minLength, or, required, sameAs } from "@/locale/locale.config";
import { SignUp } from "@/models/auth.model";
import { AuthRest } from "@/services/rest/auth.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { validateCPF, validateName } from "@/utils/regex.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import useVuelidate from "@vuelidate/core";
import { defineComponent, defineAsyncComponent } from "vue";

const ComputerComponent = defineAsyncComponent(
  () => import("../Login/computer-component.vue")
);

const authRest = new AuthRest();
export default defineComponent({
  name: "signUpComplete",
  components: { ComputerComponent },
  computed: {
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  data() {
    return {
      form: new SignUp(),
      token: "" as string,
      hasError: false as boolean,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        name: {
          required,
          validName: or((value: string) => validateName(value)),
        },
        cpf: {
          required,
          validCPF: or((value: string) => validateCPF(value)),
        },
        password: { required, min: minLength(3) },
        confirmPassword: { sameAs: sameAs(this.form.password) },
      },
    };
  },
  mounted() {
    this.validateToken();
  },
  methods: {
    sendEmailToUser(): void {
      this.v$.form.$touch();
      if (this.v$.form.$error) return;

      this.token = String(this.$route.params.id);
      authRest
        .signUpUserComplete(
          { ...this.form, cpf: this.form.cpf?.replace(/[^\d]+/g, "") },
          this.token
        )
        .subscribe({
          next: () => {
            this.$toast.add(ToastFactory.success(Messages.SIGNUP_SUCCESS));
            this.go.redirectTo("sign-in");
          },
          error: () => {
            this.hasError = true;
          },
        });
    },
    validateToken(): void {
      this.token = String(this.$route.params.id);
      authRest.validateToken(this.token).subscribe({
        error: () => {
          this.hasError = true;
        },
      });
    },
  },
});
</script>

<style lang="scss">
#custom-register-svg {
  svg,
  path {
    width: 100%;
    height: 300px;
    @apply fill-current #{!important};
  }
}
</style>
